"use client";

import { PaletteEffect } from "@/components/PaletteEffect";
import { useDocumentTitle } from "@/lib/use-document-title";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { NuqsAdapter } from "nuqs/adapters/next/app";
import { type ReactNode, useEffect, useState } from "react";
import { Toaster } from "./ui/sonner";

// Next dev HMR sometimes leaves a tab holding a chunk reference that
// the dev server has since rebuilt — clicking around (or just landing
// on a freshly-restarted dev server) then triggers a runtime
// "Loading chunk … failed" overlay. Disk + dev server are fine; only
// the cached client manifest is stale.
//
// Strategy: a small reload budget kept in ``sessionStorage`` rather
// than a one-shot flag. The previous one-shot implementation was too
// brittle — once the user hit ONE chunk error in a tab's lifetime, the
// auto-recovery refused to fire again even though every subsequent
// chunk error was just as recoverable (e.g., separate dev-server
// restarts during a coding session). With a budget + a "page lived
// for ≥3 s" health gate that resets the counter, we get:
//   - genuine broken chunks loop ≤ MAX_RELOADS times and stop
//   - independent dev-restart drifts each get their own reload
//   - hydration completes quietly → budget resets
const MAX_RELOADS = 3;
const HEALTH_RESET_MS = 3000;

function useChunkLoadErrorRecovery(): void {
  useEffect(() => {
    const KEY = "vc:chunk-reload-count";
    function readCount(): number {
      const raw = sessionStorage.getItem(KEY);
      const n = raw ? Number.parseInt(raw, 10) : 0;
      return Number.isFinite(n) && n >= 0 ? n : 0;
    }
    function isChunkError(err: unknown): boolean {
      if (!err) return false;
      const msg = err instanceof Error ? err.message : String(err);
      const name = err instanceof Error ? err.name : "";
      return (
        name === "ChunkLoadError" ||
        /Loading chunk \S+ failed/i.test(msg) ||
        /Loading CSS chunk \S+ failed/i.test(msg)
      );
    }
    function recover(label: string, err: unknown): void {
      if (!isChunkError(err)) return;
      const n = readCount();
      if (n >= MAX_RELOADS) {
        console.warn(`[chunk-recovery] exhausted ${MAX_RELOADS} reloads; surfacing error`, err);
        return;
      }
      sessionStorage.setItem(KEY, String(n + 1));
      console.warn(
        `[chunk-recovery] reloading after ${label} (attempt ${n + 1}/${MAX_RELOADS})`,
        err,
      );
      window.location.reload();
    }
    const onError = (e: ErrorEvent) => recover("error", e.error ?? e.message);
    const onReject = (e: PromiseRejectionEvent) => recover("unhandledrejection", e.reason);
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onReject);

    // If the page hydrates cleanly + stays alive for HEALTH_RESET_MS,
    // assume chunks are fine and reset the budget. So a future dev
    // restart later in the session can still trigger recovery.
    const healthTimer = window.setTimeout(() => {
      if (readCount() > 0) {
        sessionStorage.removeItem(KEY);
      }
    }, HEALTH_RESET_MS);

    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onReject);
      window.clearTimeout(healthTimer);
    };
  }, []);
}

// One client-direct whoami ping per session.
//
// Two jobs in this hook:
//
// 1. CSRF heal — ``/api/auth/whoami`` re-issues ``vc_csrf`` on every
//    call so a stale cookie (e.g., signed with an earlier
//    ``CSRF_SECRET``) gets repaired without the user re-logging-in.
//    Layout RSC already calls whoami SSR-side, but Next.js doesn't
//    forward ``Set-Cookie`` from RSC fetches to the browser response,
//    so the BROWSER's cookie value stays stale until a client-direct
//    call lands. This hook fires that call once per tab.
//
// 2. Session reaper — if whoami returns 401, the browser is carrying
//    a session cookie that no longer maps to any Redis row (idle
//    expiry, manual revoke, Redis FLUSH, etc.). The middleware can't
//    catch this because it only checks for *presence* of the cookie,
//    not validity. Without intervention every subsequent POST 401s
//    silently — chat send, /review approve, agent-run triggers, etc.
//    Redirect to /login?next=<current> so the user re-authenticates
//    once and everything downstream works.
// Cross-tab login sync. When the operator logs in on tab A (which
// broadcasts ``{type: "logged-in"}`` on the "vc-auth" BroadcastChannel,
// see LoginCard.tsx), every other open tab in the same browser also
// picks up the message and reloads itself — so tabs sitting on some
// page whose session already 401'd get re-hydrated with the fresh
// session + CSRF cookies without a manual refresh. Mirrors the "hard
// navigation" reasoning in LoginCard.tsx: server-rendered layouts +
// RSC fetches on tab B were built under the stale cookies; only a
// reload guarantees they re-run with the new ones.
//
// The listener is a no-op on /login itself — LoginCard.tsx already
// handles its own reload path there.
function useCrossTabAuthRefresh(): void {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const path = window.location.pathname;
    if (path.startsWith("/login") || path.startsWith("/bootstrap")) return;
    let bc: BroadcastChannel | null = null;
    const onMessage = async (e: MessageEvent) => {
      if (e.data?.type !== "logged-in") return;
      // Slight delay so the broadcast side's Set-Cookie roundtrip has
      // definitely committed before we probe whoami — otherwise on a
      // very fast local network we can beat the sibling tab's cookie
      // commit.
      await new Promise((r) => setTimeout(r, 150));
      // Only reload if THIS tab is currently unauthed. Otherwise the
      // broadcast is a no-op and the operator's active work (unsaved
      // form state, scroll position, in-flight streams) is preserved.
      // Prior behaviour reloaded every non-/login tab on any broadcast,
      // which yanked the tab the operator had JUST logged into back to
      // the top and blew away sibling tabs that were already fine.
      try {
        const r = await fetch("/api/auth/whoami", {
          credentials: "include",
          cache: "no-store",
          headers: { Accept: "application/json" },
        });
        if (r.status === 401) {
          window.location.reload();
        }
      } catch {
        /* network blip — leave the tab as-is; user can reload manually */
      }
    };
    if (typeof BroadcastChannel !== "undefined") {
      try {
        bc = new BroadcastChannel("vc-auth");
        bc.addEventListener("message", onMessage);
      } catch {
        bc = null;
      }
    }
    return () => {
      bc?.removeEventListener("message", onMessage);
      bc?.close();
    };
  }, []);
}

function useCsrfRefresh(): void {
  useEffect(() => {
    const KEY = "vc:csrf-refreshed";
    if (sessionStorage.getItem(KEY)) return;
    sessionStorage.setItem(KEY, "1");
    void fetch("/api/auth/whoami", {
      credentials: "include",
      cache: "no-store",
    })
      .then((res) => {
        if (res.status === 401) {
          const path = window.location.pathname;
          // Skip the redirect on any surface that intentionally serves
          // unauthenticated visitors. Without this the anonymous
          // recipient of a ``/share/<token>`` link would see the share
          // page briefly, then get bounced to ``/login?next=/share/…``
          // by this hook (which fires from the root layout on every
          // page load). ``/login`` + ``/bootstrap`` are the sign-in
          // flow itself; ``/share`` is the tokenised public viewer;
          // ``/accept`` is invitation redemption, where the visitor has
          // no account yet by definition (middleware allowlists the same
          // set — keep in lockstep).
          //
          // ``/accept`` was the exact failure this comment warned about:
          // the invitee saw the token form render, then got replaced by
          // the login card a second later when this hook's whoami came
          // back 401. Fixing the middleware alone wasn't enough — both
          // lists have to agree (changelog 0580).
          const localeStripped = path.replace(/^\/[a-z]{2}(?:-[A-Z]{2})?(\/|$)/, "/");
          if (
            localeStripped.startsWith("/login") ||
            localeStripped.startsWith("/bootstrap") ||
            localeStripped.startsWith("/share") ||
            localeStripped.startsWith("/accept")
          ) {
            return;
          }
          const next = `${path}${window.location.search}`;
          const url = `/login?next=${encodeURIComponent(next)}`;
          // Replace so the broken page doesn't sit in history.
          window.location.replace(url);
        }
      })
      .catch(() => {
        // Network blip — leave CSRF stale; user-driven navigation
        // will trigger the same path on the next load.
        sessionStorage.removeItem(KEY);
      });
  }, []);
}

export function Providers({ children }: { children: ReactNode }) {
  // Per Tanstack docs: create the client inside the component with
  // ``useState`` so each Provider instance owns its own client. The
  // module-singleton pattern this replaced was fragile during the SSR
  // pass for client components — the QueryClient context didn't always
  // propagate, so child ``useQueryClient()`` calls threw "No QueryClient
  // set" during server rendering and Next.js fell back to CSR with a
  // Recoverable Error overlay.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Per docs/12-frontend.md §9: entity list 30s, backlinks 5min,
            // detail 2min. Default here is conservative; per-query
            // overrides set actual values.
            staleTime: 30 * 1000,
            gcTime: 5 * 60 * 1000,
            refetchOnWindowFocus: false,
            retry: 1,
          },
          mutations: {
            retry: 0,
          },
        },
      }),
  );
  useChunkLoadErrorRecovery();
  useCsrfRefresh();
  useCrossTabAuthRefresh();
  useDocumentTitle();
  return (
    <QueryClientProvider client={queryClient}>
      <NuqsAdapter>
        <PaletteEffect />
        {children}
        <Toaster />
      </NuqsAdapter>
    </QueryClientProvider>
  );
}
